'use client';
import type { Entry } from 'contentful';
import React, { useRef } from 'react';

import useIntersectionObserver from '@/hooks/useIntersectionObserver';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { TypeVideoFields, TypeMuxVideoFields } from '@/core/generated-types';

interface VideoSideBySideProps {
  mediaDesktop?: Entry<TypeVideoFields | TypeMuxVideoFields>;
  mediaMobile?: Entry<TypeVideoFields | TypeMuxVideoFields>;
}

const VideoSideBySide = ({
  mediaDesktop,
  mediaMobile,
}: VideoSideBySideProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const isDesktop = useIsDesktop();
  const media = isDesktop ? mediaDesktop : mediaMobile;

  useIntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (videoRef.current) {
        if (entry.isIntersecting) {
          const newSrc =
            media && media?.fields && 'video' in media?.fields
              ? media?.fields?.video?.fields?.file?.url
              : '';
          const hasSrcChanged =
            newSrc && !videoRef.current.src.includes(newSrc);
          if (hasSrcChanged) {
            videoRef.current.src = newSrc;
            // Force video element to reload its source
            videoRef.current.load();
            return;
          }
          videoRef.current.play();
        } else {
          videoRef.current.pause();
        }
      }
    });
  }, videoRef);

  return (
    <div className="lg:w-1/2 relative aspect-video lg:aspect-square bg-slate-300">
      <video
        data-testid="side-by-side-list-video"
        ref={videoRef}
        className="aspect-video lg:aspect-square"
        playsInline
        autoPlay
        loop
        muted
      ></video>
    </div>
  );
};

export default VideoSideBySide;
